// src/pages/Stock/ProductStockModal.js
import React, { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useForm } from 'react-hook-form';
import { stockService, productService } from '../../services/api';
import { toast } from 'react-toastify';
import SearchableSelect from "../../components/ui/SearchableSelect";

const ProductStockModal = ({ isOpen, onClose, onSuccess, initialData = null }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [products, setProducts] = useState([]);
    const [isAddingStock, setIsAddingStock] = useState(true); // true for adding, false for removing
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [loadingProducts, setLoadingProducts] = useState(false);

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        watch,
        setValue,
        getValues,
    } = useForm({
                    defaultValues: {
                        product_id: '',
                        variant: 'standard',
                        quantity: 1,
                        notes: ''
                    }
                });

    const watchProductId = watch('product_id');
    const watchVariant = watch('variant');

    // Fetch products when modal opens
    useEffect(() => {
        if (isOpen) {
            fetchProducts();

            // If we're editing existing stock item
            if (initialData) {
                setValue('product_id', initialData.product_id);
                setValue('variant', initialData.variant);
                // For editing, we show the current quantity - not how much to add/remove
                setValue('quantity', initialData.quantity);
                setSelectedProduct({
                                       id: initialData.product_id,
                                       name: initialData.product?.name || ''
                                   });
            } else {
                reset();
                setIsAddingStock(true);
            }
        }
    }, [isOpen, initialData, reset, setValue]);

    const fetchProducts = async () => {
        try {
            setLoadingProducts(true);
            const response = await productService.getProducts();
            setProducts(response.data || []);
        } catch (error) {
            console.error('Error fetching products:', error);
            toast.error('Failed to load products');
        } finally {
            setLoadingProducts(false);
        }
    };

    // Handle product selection
    const handleProductChange = (productId) => {
        setValue('product_id', productId, { shouldValidate: true });
        const product = products.find(p => p.id === parseInt(productId));
        setSelectedProduct(product);
    };

    const onSubmit = async (data) => {
        setIsLoading(true);

        try {
            // Convert values to appropriate types
            data.product_id = parseInt(data.product_id);
            data.quantity = parseInt(data.quantity);

            if (initialData) {
                // If editing existing stock, use adjust endpoint
                try {
                    await stockService.adjustStock({
                                                       stock_id: initialData.id,
                                                       quantity: data.quantity,
                                                       notes: data.notes,
                                                       force: false // Default to non-force
                                                   });
                    toast.success('Stock adjusted successfully');
                    onSuccess();
                    onClose();
                } catch (error) {
                    // Check if we need to use force
                    if (error.response?.data?.requires_force) {
                        if (window.confirm('Some of this stock has been allocated to fulfilled orders. Removing it may cause inconsistencies. Are you sure you want to continue?')) {
                            // User confirmed, retry with force
                            await stockService.adjustStock({
                                                               stock_id: initialData.id,
                                                               quantity: data.quantity,
                                                               notes: data.notes + ' (Forced removal)',
                                                               force: true
                                                           });
                            toast.success('Stock adjusted successfully with force option');
                            onSuccess();
                            onClose();
                        }
                    } else {
                        // Re-throw other errors
                        throw error;
                    }
                }
            } else {
                // For new stock, use the appropriate endpoint based on whether adding or removing
                if (isAddingStock) {
                    // Adding new stock
                    await stockService.addStock({
                                                    product_id: data.product_id,
                                                    variant: data.variant,
                                                    quantity: data.quantity,
                                                    source: 'manual',
                                                    notes: data.notes
                                                });
                    toast.success('Stock added successfully');
                    onSuccess();
                    onClose();
                } else {
                    // Removing stock - We'll find the stock first, then adjust it
                    // First, we need to fetch current stock for this product/variant
                    const stockResponse = await stockService.getStock({
                                                                          search: selectedProduct?.sku || '',
                                                                          variant: data.variant
                                                                      });

                    const stockItem = stockResponse.data?.data?.find(
                        item => item.product_id === data.product_id && item.variant === data.variant
                    );

                    if (!stockItem) {
                        throw new Error('No stock found for this product and variant');
                    }

                    // Calculate new quantity
                    const newQuantity = Math.max(0, stockItem.quantity - data.quantity);

                    try {
                        await stockService.adjustStock({
                                                           stock_id: stockItem.id,
                                                           quantity: newQuantity,
                                                           notes: `Manual removal: ${data.notes}`,
                                                           force: false
                                                       });
                        toast.success('Stock removed successfully');
                        onSuccess();
                        onClose();
                    } catch (error) {
                        // Check if we need to use force
                        if (error.response?.data?.requires_force) {
                            if (window.confirm('Some of this stock has been allocated to fulfilled orders. Removing it may cause inconsistencies. Are you sure you want to continue?')) {
                                // User confirmed, retry with force
                                await stockService.adjustStock({
                                                                   stock_id: stockItem.id,
                                                                   quantity: newQuantity,
                                                                   notes: `Manual removal: ${data.notes} (Forced removal)`,
                                                                   force: true
                                                               });
                                toast.success('Stock removed successfully with force option');
                                onSuccess();
                                onClose();
                            }
                        } else {
                            // Re-throw other errors
                            throw error;
                        }
                    }
                }
            }
        } catch (error) {
            console.error('Error managing stock:', error);
            toast.error(error.response?.data?.message || 'Failed to update stock');
        } finally {
            setIsLoading(false);
        }
    };

    // Toggle between add and remove modes
    const toggleMode = () => {
        setIsAddingStock(!isAddingStock);
    };

    const modalTitle = initialData
                       ? 'Adjust Stock'
                       : (isAddingStock ? 'Add Product Stock' : 'Remove Product Stock');

    return (
        <Transition.Root show={isOpen} as={Fragment}>
            <Dialog as="div" className="fixed z-10 inset-0 overflow-y-auto" onClose={onClose}>
                <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </Transition.Child>

                    {/* This element is to trick the browser into centering the modal contents. */}
                    <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
                        &#8203;
                    </span>

                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enterTo="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                        leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                            <div className="absolute top-0 right-0 pt-4 pr-4">
                                <button
                                    type="button"
                                    className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                                    onClick={onClose}
                                >
                                    <span className="sr-only">Close</span>
                                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                                </button>
                            </div>

                            <div>
                                <div className="mt-3 text-center sm:mt-0 sm:text-left">
                                    <Dialog.Title as="h3" className="text-lg leading-6 font-medium text-gray-900">
                                        {modalTitle}
                                    </Dialog.Title>
                                    <div className="mt-2">
                                        <p className="text-sm text-gray-500">
                                            {initialData
                                             ? 'Adjust the quantity for this stock item.'
                                             : (isAddingStock
                                                ? 'Add product to your stock inventory.'
                                                : 'Remove product from your stock inventory.')}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {!initialData && (
                                <div className="mt-4">
                                    <div className="bg-gray-100 p-2 rounded-md flex justify-center">
                                        <button
                                            type="button"
                                            onClick={toggleMode}
                                            className={`px-4 py-2 rounded-md ${
                                                isAddingStock
                                                ? 'bg-primary-600 text-white'
                                                : 'bg-gray-200 text-gray-800'
                                            }`}
                                        >
                                            Add Stock
                                        </button>
                                        <button
                                            type="button"
                                            onClick={toggleMode}
                                            className={`px-4 py-2 ml-2 rounded-md ${
                                                !isAddingStock
                                                ? 'bg-red-600 text-white'
                                                : 'bg-gray-200 text-gray-800'
                                            }`}
                                        >
                                            Remove Stock
                                        </button>
                                    </div>
                                </div>
                            )}

                            <form onSubmit={handleSubmit(onSubmit)} className="mt-5 space-y-4">
                                {!initialData && (
                                    <div>
                                        <label htmlFor="product_id" className="block text-sm font-medium text-gray-700 mb-1">
                                            Product *
                                        </label>

                                        <input
                                            type="hidden"
                                            {...register('product_id', { required: 'Product is required' })}
                                        />

                                        <SearchableSelect
                                            id="product_id"
                                            options={products.map(product => ({
                                                id: product.id,
                                                label: `${product.sku}`
                                            }))}
                                            value={getValues('product_id')}
                                            onChange={handleProductChange}
                                            placeholder={loadingProducts ? "Loading products..." : "Select a product"}
                                            isDisabled={loadingProducts}
                                        />

                                        {errors.product_id && (
                                            <p className="mt-1 text-sm text-red-600">{errors.product_id.message}</p>
                                        )}
                                    </div>
                                )}

                                {!initialData && (
                                    <div>
                                        <label htmlFor="variant" className="block text-sm font-medium text-gray-700">
                                            Variant *
                                        </label>
                                        <select
                                            id="variant"
                                            {...register('variant', { required: 'Variant is required' })}
                                            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                                        >
                                            <option value="standard">Standard</option>
                                            <option value="carbon">Carbon</option>
                                        </select>
                                        {errors.variant && (
                                            <p className="mt-1 text-sm text-red-600">{errors.variant.message}</p>
                                        )}
                                    </div>
                                )}

                                <div>
                                    <label htmlFor="quantity" className="block text-sm font-medium text-gray-700">
                                        {initialData
                                         ? 'New Quantity *'
                                         : (isAddingStock ? 'Quantity to Add *' : 'Quantity to Remove *')}
                                    </label>
                                    <input
                                        type="number"
                                        id="quantity"
                                        {...register('quantity', {
                                            required: 'Quantity is required',
                                            min: {
                                                value: initialData ? 0 : 1,
                                                message: initialData
                                                         ? 'Quantity must be at least 0'
                                                         : 'Quantity must be at least 1'
                                            }
                                        })}
                                        className={`mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md ${
                                            errors.quantity ? 'border-red-300' : ''
                                        }`}
                                        placeholder="Enter quantity"
                                    />
                                    {errors.quantity && (
                                        <p className="mt-1 text-sm text-red-600">{errors.quantity.message}</p>
                                    )}
                                </div>

                                <div>
                                    <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                                        Notes
                                    </label>
                                    <textarea
                                        id="notes"
                                        rows={3}
                                        {...register('notes')}
                                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                                        placeholder="Add any notes about this stock adjustment"
                                    />
                                </div>

                                <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                                    <button
                                        type="submit"
                                        disabled={isLoading}
                                        className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:col-start-2 sm:text-sm disabled:opacity-50 ${
                                            initialData || isAddingStock
                                            ? 'bg-primary-600 hover:bg-primary-700 focus:ring-primary-500'
                                            : 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                                        }`}
                                    >
                                        {isLoading
                                         ? 'Processing...'
                                         : (initialData
                                            ? 'Update Stock'
                                            : (isAddingStock ? 'Add Stock' : 'Remove Stock'))}
                                    </button>
                                    <button
                                        type="button"
                                        className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                                        onClick={onClose}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </Transition.Child>
                </div>
            </Dialog>
        </Transition.Root>
    );
};

export default ProductStockModal;
